import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { t as i18nextT } from 'i18next';
import * as S from './CardSettings.styles';
import { Card } from '@/mock-data/cards';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { DrawerItem } from '@/components/Drawer/Drawer.styles';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Snackbar, SnackbarRef } from '@/components/UI/SnackBar/Snackbar';
import { Button } from '@/components/UI/Button/Button';
import { Input } from '@/components/UI/Input/Input';
import { CARD_NAME_MAX_LENGTH } from '@/components/Profile/Cards/Cards.constants';

export const renameCardFormSchema = z.object({
  name: z
    .string()
    .min(1, i18nextT('common.min_symbols', { n: 1 }))
    .max(18, i18nextT('common.max_symbols', { n: 18 })),
});

export type RenameCardFormFields = z.infer<typeof renameCardFormSchema>;

interface CardSettingsProps {
  isOpen: boolean;
  close: () => void;
  card: Card;
}

export const CardSettings: React.FC<CardSettingsProps> = ({
  isOpen,
  close,
  card,
}) => {
  const { t } = useTranslation();
  const [isRemoveOpen, removeToggle] = useToggleOpenState();
  const [isRenameOpen, renameToggle] = useToggleOpenState();
  const snackbarRef = useRef<SnackbarRef>(null);
  const { control, handleSubmit, reset } = useForm<RenameCardFormFields>({
    resolver: zodResolver(renameCardFormSchema),
    defaultValues: { name: '' },
  });

  function startRemove() {
    close();
    removeToggle();
  }

  function remove() {
    removeToggle(false);
    snackbarRef.current?.open(
      t('pages.cabinet.card_is_removed', { name: card?.name })
    );
  }

  function startRename() {
    reset();
    close();
    renameToggle();
  }

  function rename(data: RenameCardFormFields) {
    console.log('rename', data);
    renameToggle(false);
    snackbarRef.current?.open(
      t('pages.cabinet.card_is_renamed', { name: card?.name })
    );
  }

  function makeMain() {
    close();
    snackbarRef.current?.open(
      t('pages.cabinet.card_is_selected_as_main', { name: card?.name })
    );
  }

  useEffect(() => {
    if (card) {
      reset({ name: card.name });
    }
  }, [card]);

  return (
    <>
      <BottomSheet isOpen={isOpen} close={close} title={card.name}>
        {!card.expirationDate && (
          <>
            {!card.isMain && (
              <DrawerItem $isFirst onClick={makeMain}>
                <IconBox $filename="star.svg" $width={35} $height={35} />
                <S.Text $variant="1" as="span">
                  {t('pages.cabinet.make_main')}
                </S.Text>
              </DrawerItem>
            )}

            <DrawerItem onClick={startRename}>
              <IconBox $filename="edit.svg" $width={35} $height={35} />
              <S.Text $variant="1" as="span">
                {t('pages.cabinet.rename_card')}
              </S.Text>
            </DrawerItem>
          </>
        )}

        <DrawerItem $isLast onClick={startRemove}>
          <IconBox $filename="trash.svg" $width={35} $height={35} />
          <S.Text $variant="1" as="span" $isRed>
            {t('pages.cabinet.remove_card')}
          </S.Text>
        </DrawerItem>
      </BottomSheet>

      <BottomSheet
        isOpen={isRenameOpen}
        close={renameToggle}
        title={t('pages.cabinet.rename_card')}
      >
        <S.Form onSubmit={handleSubmit(rename)}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                maxLength={CARD_NAME_MAX_LENGTH}
                label={t('pages.cabinet.card_name')}
                helperText={fieldState.error?.message}
                hasError={!!fieldState.error}
              />
            )}
          />
          <Button type="submit" appearance="secondary" isFullWidth>
            {t('common.save')}
          </Button>
        </S.Form>
      </BottomSheet>

      <Popup
        isOpen={isRemoveOpen}
        close={removeToggle}
        title={t('pages.cabinet.are_you_sure_you_want_to_remove_saved_card')}
        actions={{
          left: {
            isGray: true,
            text: t('common.decline'),
            onClick: removeToggle,
          },
          right: {
            isRed: true,
            text: t('common.remove'),
            onClick: remove,
          },
        }}
      />

      <Snackbar ref={snackbarRef} />
    </>
  );
};
